/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	Tuple,
	Tuple2,
}	from "ts-base/tuple";
import { Functions }	from "ts-base/functions";
import { Fn, Fn0, Fn2 }	from "ts-base/fn";
import { Is }			from "ts-base/is";
import { Predicate }	from "ts-base/predicate";
import { Arrays }		from "ts-base/arrays";
import { Nea }			from "ts-base/nea";
import { Maybe }		from "ts-base/maybe";
import { Validated }	from "ts-base/validated";

export type Validator<S, T>	= Fn<S, Validator.Result<T>>;

export namespace Validator {
	export type Result<T>		= Validated<Problem, T>;
	export type Problem			= string;

	export type Input<X> = X extends Validator<infer I, any> ? I : never;
	export type Output<X> = X extends Validator<any, infer O> ? O : never;

	//-----------------------------------------------------------------------------
	//## trivial functions

	export const delay	= <S, T>(self:Fn0<Validator<S, T>>):Validator<S, T>	=>
		Functions.delay(self);

	export const identity	= <T>():Validator<T, T>				=> Validated.valid;
	export const constant	= <S, T>(value:T):Validator<S, T>	=> Fn.constant(Validated.valid(value));

	//-----------------------------------------------------------------------------
	//## alternative

	export const orElse	= <S, T>(v1:Validator<S, T>, v2:Validator<S, T>):Validator<S, T>	=>
		it => Validated.orElse(v1(it))(v2(it));

	//-----------------------------------------------------------------------------
	//## error

	export const mapProblems	= <S, T>(base:Validator<S, T>) => (func:Fn<Problem, Problem>):Validator<S, T>	=>
		it => Validated.mapErrors(base(it))(func);

	export const rebrandProblems	= <S, T>(base:Validator<S, T>, brandName:string):Validator<S, T>	=>
		it => Validated.mapErrors(base(it))(prefixProblem(`expected a valid ${brandName}`));

	//-----------------------------------------------------------------------------
	//## contravariant functor

	export const contraMap	= <S, T>(base:Validator<S, T>) => <R>(func:Fn<R, S>):Validator<R, T>	=>
		Validated.contraMapKleisli(base)(func);

	//-----------------------------------------------------------------------------
	//## functor

	export const map	= <S, T>(base:Validator<S, T>) => <U>(func:Fn<T, U>):Validator<S, U>	=>
		Validated.mapKleisli(base)(func);

	//-----------------------------------------------------------------------------
	//## applicative functor

	/** never fails, just applies a function */
	export const unfailing	= <S, T>(func:Fn<S, T>):Validator<S, T>	=>
		Validated.pureKleisli(func);

	export const merge	= <S, T1 extends object, T2 extends object>(v1:Validator<S, T1>, v2:Validator<S, T2>):Validator<S, T1 & T2>	=>
		map2(v1, v2)((t1, t2) => ({ ...t1, ...t2 }));

	export const map2	= <S, T1 extends object, T2 extends object>(v1:Validator<S, T1>, v2:Validator<S, T2>) => <U>(func:Fn2<T1, T2, U>):Validator<S, U>	=>
		input => Validated.mapN(v1(input), v2(input))(func);

	export const tuple2	= <S, T1 extends object, T2 extends object>(v1:Validator<S, T1>, v2:Validator<S, T2>):Validator<S, Tuple2<T1, T2>>	=>
		map2(v1, v2)(Tuple.ofN);

	// TODO nary add andThenN/mapN/tupleN

	export type AnySelfRecord	= Record<string, Validator<any, any>>;

	export type SelfRecord<S, T>	= {
		[K in keyof T]: Validator<S, T[K]>;
	};

	export type InputRecord<T>	= {
		[K in keyof T]: Input<T[K]>
	};

	// return type of a schema validator
	export type OutputRecord<T>	= {
		[K in keyof T]: Output<T[K]>;
	};

	export const object	= <const T extends AnySelfRecord>(validators:T):Validator<InputRecord<T>, Readonly<OutputRecord<T>>>	=>
		(input:InputRecord<T>) => {
			const out	= {} as any;
			for (const key in validators) {
				const raw		= (input as any)[key];
				const validated	= validators[key](raw);
				const result	= Validated.mapErrors(validated)((e) => `field "${key}": ${e}`);
				out[key]	= result;
			}
			return Validated.object(out) as any;
		};

	//-----------------------------------------------------------------------------
	//## pseudo monad

	export const andThen = <R, S, T>(a:Validator<R, S>, b:Validator<S, T>):Validator<R, T> =>
		Validated.andThen(a, b);

	export const andThen3 = <R, S, T, U>(a:Validator<R, S>, b:Validator<S, T>, c:Validator<T, U>):Validator<R, U> =>
		Validated.andThen3(a, b, c);

	export const andThen4 = <R, S, T, U, V>(a:Validator<R, S>, b:Validator<S, T>, c:Validator<T, U>, d:Validator<U, V>):Validator<R, V> =>
		Validated.andThen4(a, b, c, d);

	//-----------------------------------------------------------------------------
	//## basic validation

	// TODO validation use an error tree instead of repeating prefixes?
	export const fields	= <S, V, T extends Record<string, unknown>>(spec:SelfRecord<V, T>, extract:Fn<string, Validator<S, V>>):Validator<S, Readonly<T>>	=>
		(input:S) => {
			const out	= {} as any;
			for (const key in spec) {
				const existing	= extract(key)(input);
				const validated	= Validated.then(existing)(spec[key]);
				const result	= Validated.mapErrors(validated)((e) => `field "${key}": ${e}`);
				out[key]	= result;
			}
			return Validated.object(out) as any;
		};

	export const array	= <S, T>(base:Validator<S, T>):Validator<ReadonlyArray<S>, ReadonlyArray<T>>	=> (input:ReadonlyArray<S>) =>
		Arrays.sequenceValidated(
			input.map((s, idx) =>
				Validated.mapErrors(base(s))((e) =>
					`element ${idx}: ${e}`
				)
			)
		);

	export const nea	= <S, T>(base:Validator<S, T>):Validator<Nea<S>, Nea<T>>	=> (input:Nea<S>) =>
		Nea.sequenceValidated(
			Nea.mapWithIndex(input)((s, idx) =>
				Validated.mapErrors(base(s))((e) =>
					`element ${idx}: ${e}`
				)
			)
		);

	/*
	export const record	= <R, K extends string, V>(key:Validator<string,K>, value:Validator<R,V>):Validator<Record<string,R>,Record<K,V>> => (input:Record<string,R>) => {
		const output	= {} as Record<K,V>;
		const errors:Array<Problem>	= [];
		for (const [ k, v ] of Object.entries(input)) {
			const kv	=
				Validated.tupleN(
					Validated.mapErrors(key(k))	(it => `in key ${k}: ${it}`),
					ValidValidatedateds.mapErrors(value(v))	(it => `in value of ${k}: ${it}`),
				);
			if (Validated.isValid(kv))	{
				const [ kk, vv ]	= kv.value;
				output[kk]	= vv;
			}
			else {
				Arrays.pushAll(errors)(kv.errors);
			}
		}
		return Validated.unlessErrors(errors)(output);
	};
	*/

	export const maybe	= <S, T>(base:Validator<S, T>):Validator<Maybe<S>, Maybe<T>>	=>
		input => Maybe.traverseValidated(input)(base);

	export const oneOf	= <S, T extends ReadonlyArray<Validator<S, any>>>(...partials:T):Validator<S, Output<T[number]>>	=>
		input => {
			const allErrors:Array<Problem>	= [];
			let index	= 0;
			for (const partial of partials) {
				const typed:Validator<S, Output<T[number]>> = partial;
				const validated	= typed(input);
				if (Validated.isValid(validated))	return validated;
				const errors	= validated.errors.map((e) => `alternative ${index}: ${e}`);
				Arrays.pushAll(allErrors)(errors);
				index++;
			}
			return Validated.invalid(Nea.unsafeFromArray(allErrors));
		};

	/** allow null values, passing them on unmodified */
	export const nullable	= <S, T>(base:Validator<S, T>):Validator<S|null, T|null>	=>
		input => input !== null ? base(input) : Validated.valid(null);

	export const notNull	= <T>():Validator<T|null, T>	=> (it:T|null)	=>
		it !== null ? Validated.valid(it) : Validated.invalid1("expected non-null value");

	export const notUndefined	= <T>():Validator<T|undefined, T>	=> (it:T|undefined)	=>
		it !== undefined ? Validated.valid(it) : Validated.invalid1("expected non-undefined value");

	export const nonEmpty	= <T>():Validator<ReadonlyArray<T>, Nea<T>>	=>
		input => Validated.fromNullable(Nea.fromArray(input))("expected at least one element");

	export const exactly	= <S, T extends string|number|boolean|null>(tag:T):Validator<S, T> =>
		input => (input as unknown as T) === tag ? Validated.valid(tag) : Validated.invalid1(`expected exactly ${stringifyValue(tag)}, but got ${stringifyValue(input)}`);

	export const enumValue	= <S, T extends S>(values:ReadonlyArray<T>):Validator<S, T>	=>
		input	=> values.includes(input as T) ? Validated.valid(input as T) : Validated.invalid1(`expected one of ${values.map(stringifyValue).join(", ")}`);

	export const enumValueNamed	= <S, T extends S>(name:string, values:ReadonlyArray<T>):Validator<S, T>	=>
		input	=> values.includes(input as T) ? Validated.valid(input as T) : Validated.invalid1(`expected a valid ${name}`);

	export const mapGet	= <K extends PropertyKey, V>(name:string, map:ReadonlyMap<K, V>):Validator<K, V>	=> (key:K) =>
		map.has(key)
		?	Validated.valid(map.get(key) as V)
		:	Validated.invalid1(`key ${key.toString()} not found in ${name}`);

	export const setHas	= <K extends PropertyKey>(name:string, set:ReadonlySet<K>):Validator<K, K>	=> (key:K) =>
		set.has(key)
		?	Validated.valid(key)
		:	Validated.invalid1(`key ${key.toString()} not found in ${name}`);

	export const fromNullResult	= <S, T>(func:Fn<S, T|null>, problem:Problem):Validator<S, T>	=>
		input => Validated.fromNullable(func(input))(problem);

	export const fromMaybeResult	= <S, T>(func:Fn<S, Maybe<T>>, problem:Problem):Validator<S, T>	=>
		input => Validated.fromMaybe(func(input))(problem);

	export const rejectWith	= <T>(problems:Fn<T, ReadonlyArray<Problem>>):Validator<T, T>	=>
		it => Validated.unlessErrors(problems(it))(it);

	export const rejectUnless	= <T>(test:Predicate<T>, problem:Problem):Validator<T, T>	=>
		Validated.predicateKleisli(test, problem);

	export const filterIs	= <S, T extends S>(filter:Is<S, T>, problem:Problem):Validator<T, T>	=>
		it => filter(it) ? Validated.valid(it) : Validated.invalid1(problem);

	export const constrainNumber	= <T extends number>(minimum:number|null, maximum:number|null):Validator<T, T>	=>
		rejectWith(it => {
			const problems	= [];
			if (minimum !== null && it < minimum)	problems.push(`expected at least ${minimum}`);
			if (maximum !== null && it > maximum)	problems.push(`expected at most ${maximum}`);
			return problems;
		});

	//-----------------------------------------------------------------------------
	//## error annotation helpers

	export const annotateKey	= <T extends Record<string, Validated<string, unknown>>>(input:T):T	=> {
		const result	= {} as any;
		for (const k in input) {
			const raw		= input[k];
			const annotated	= Validated.mapErrors(raw)((error) => `field "${k}": ${error}`);
			result[k]		= annotated;
		}
		return result;
	};

	export const annotateIndex	= <T>(input:ReadonlyArray<Validated<string, T>>):Array<Validated<string, T>>	=>
		input.map((item, index) =>
			Validated.mapErrors(item)((error) => `element ${index}: ${error}`)
		);

	export const annotateValue	= <S, T>(base:Validator<S, T>):Validator<S, T>	=>
		it => Validated.mapErrors(base(it))((error) => `${error}: got value ${stringifyValue(it)}`);

	const prefixProblem	= (message:string)	=> (problem:Problem):Problem	=>
		`${message}: ${problem}`;

	// TODO validation ugly stringification
	const stringifyValue	= (it:unknown):string	=> String(it);
}
